import { useState, useEffect } from 'react';
import Navigation from '@/components/Navigation';
import { MapPin, Award, Play, Search, Filter, ExternalLink, Sparkles, Mountain, FileText, Printer, X, BookOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { api } from '@/App';
import { toast } from 'sonner';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix for default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom icons for BLM (blue), Forest Service (green), and BLM Corrals (orange)
const blmIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const forestServiceIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const blmCorralIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-orange.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Component to fit map bounds to markers
const FitBounds = ({ hmas }) => {
  const map = useMap();
  
  useEffect(() => {
    if (hmas.length > 0) {
      const validHmas = hmas.filter(h => h.lat && h.lon);
      if (validHmas.length > 0) {
        const bounds = validHmas.map(h => [h.lat, h.lon]);
        map.fitBounds(bounds, { padding: [50, 50], maxZoom: 6 });
      }
    }
  }, [hmas, map]);
  
  return null;
};

const Learn = ({ user, logout }) => {
  const [hmas, setHmas] = useState([]);
  const [filteredHmas, setFilteredHmas] = useState([]);
  const [selectedState, setSelectedState] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [resources, setResources] = useState([]);
  const [selectedHma, setSelectedHma] = useState(null);
  const [showHmaDialog, setShowHmaDialog] = useState(false);
  const [quizzes, setQuizzes] = useState([]);
  const [selectedQuiz, setSelectedQuiz] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [score, setScore] = useState(0);
  const [quizStarted, setQuizStarted] = useState(false);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [quizProgress, setQuizProgress] = useState({ completed_quizzes: [], quiz_details: [] });
  
  // Mustang Care Quiz states
  const [mustangQuizStarted, setMustangQuizStarted] = useState(false);
  const [mustangQuizCompleted, setMustangQuizCompleted] = useState(false);
  const [selectedMustangQuiz, setSelectedMustangQuiz] = useState(null);
  const [currentMustangQuestion, setCurrentMustangQuestion] = useState(0);
  const [currentMustangAnswer, setCurrentMustangAnswer] = useState(null);
  const [mustangScore, setMustangScore] = useState(0);
  const [showPointsEarned, setShowPointsEarned] = useState(false);
  const [mustangCareQuizzes, setMustangCareQuizzes] = useState([]);
  const [viewMode, setViewMode] = useState('quiz'); // 'quiz' or 'study-guide'
  const [selectedModuleForGuide, setSelectedModuleForGuide] = useState(null);

  const states = [
    "Arizona", "California", "Colorado", "Idaho", "Montana",
    "Nevada", "New Mexico", "Oregon", "Utah", "Wyoming"
  ];

  useEffect(() => {
    fetchHmas();
    fetchQuizzes();
    fetchMustangQuizzes();
    fetchResources();
    fetchQuizProgress();
  }, []);

  useEffect(() => {
    filterHmas();
  }, [hmas, selectedState, searchTerm]);

  const fetchQuizProgress = async () => {
    try {
      const response = await api.get('/education/quiz-progress');
      setQuizProgress(response.data);
    } catch (error) {
      console.error('Error fetching quiz progress:', error);
    }
  };

  const isQuizCompleted = (quizId) => {
    return quizProgress.completed_quizzes?.includes(quizId);
  };

  const getQuizDetails = (quizId) => {
    return quizProgress.quiz_details?.find(d => d.quiz_id === quizId);
  };

  const fetchHmas = async () => {
    try {
      const response = await api.get('/education/hmas');
      setHmas(response.data);
    } catch (error) {
      console.error('Error fetching HMAs:', error);
    }
  };

  const filterHmas = () => {
    let filtered = [...hmas];
    
    // Filter by state
    if (selectedState !== 'all') {
      filtered = filtered.filter(hma => hma.state === selectedState);
    }
    
    // Filter by search term
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(hma => 
        hma.name.toLowerCase().includes(search) ||
        hma.state.toLowerCase().includes(search)
      );
    }
    
    // Sort by name
    filtered.sort((a, b) => a.name.localeCompare(b.name));
    
    setFilteredHmas(filtered);
  };

  const fetchQuizzes = async () => {
    try {
      const response = await api.get('/education/trivia');
      setQuizzes(response.data);
    } catch (error) {
      console.error('Error fetching quizzes:', error);
    }
  };

  const fetchMustangQuizzes = async () => {
    try {
      // Use native fetch to bypass any axios issues
      const timestamp = Date.now();
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/quiz-modules?_t=${timestamp}`;
      
      const response = await fetch(url, {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('[QUIZ] Fetched', data.length, 'modules');
      console.log('[QUIZ] All module titles:', data.map(m => m.title));
      
      // Log HMA specifically
      const hma = data.find(m => m.title?.includes('HMA'));
      if (hma) {
        console.log('[QUIZ] HMA has', hma.questions?.length, 'questions');
      }
      
      // Log the 3 specific modules user is looking for
      const wildHorse = data.find(m => m.title === 'Wild Horse Territories');
      const forestService = data.find(m => m.title === 'Forest Service: WHB');
      const blmOffRange = data.find(m => m.title === 'BLM Off-Range Corrals');
      console.log('[QUIZ] Wild Horse Territories found:', !!wildHorse);
      console.log('[QUIZ] Forest Service: WHB found:', !!forestService);
      console.log('[QUIZ] BLM Off-Range Corrals found:', !!blmOffRange);
      
      setMustangCareQuizzes(data || []);
    } catch (error) {
      console.error('[QUIZ] Error fetching mustang quizzes:', error);
      toast.error('Failed to load quiz modules');
    }
  };

  const fetchResources = async () => {
    try {
      const response = await api.get('/resources');
      setResources(response.data);
    } catch (error) {
      console.error('Error fetching resources:', error);
    }
  };

  const startQuiz = (quiz) => {
    setSelectedQuiz(quiz);
    setQuizStarted(true);
    setCurrentQuestion(0);
    setScore(0);
    setQuizCompleted(false);
  };

  const answerQuestion = async (answerIndex) => {
    const question = selectedQuiz.questions[currentQuestion];
    const isCorrect = answerIndex === question.correct;
    
    if (isCorrect) {
      setScore(score + 1);
    }
    
    // Award coins and XP immediately for the answer
    try {
      const response = await api.post('/education/answer-question', {
        module_id: selectedQuiz.id,
        question_id: question.id || `q_${currentQuestion}`,
        selected_answer: answerIndex,
        is_correct: isCorrect
      });
      
      if (response.data.coins_earned > 0) {
        toast.success(`+${response.data.coins_earned} coins, +${response.data.xp_earned} XP! 🎉`, {
          duration: 2000,
          icon: '🪙'
        });
      }
    } catch (error) {
      console.error('Failed to record answer:', error);
    }

    if (currentQuestion + 1 < selectedQuiz.questions.length) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      submitQuiz();
    }
  };

  // Mustang Care Quiz Functions
  const startMustangQuiz = (quiz) => {
    setSelectedMustangQuiz(quiz);
    setCurrentMustangQuestion(0);
    setCurrentMustangAnswer(null);
    setMustangScore(0);
    setMustangQuizStarted(true);
    setMustangQuizCompleted(false);
  };

  const answerMustangQuestion = async (answerIndex) => {
    setCurrentMustangAnswer(answerIndex);
    const question = selectedMustangQuiz.questions[currentMustangQuestion];
    const isCorrect = answerIndex === question.correct;
    
    if (isCorrect) {
      setMustangScore(mustangScore + 1);
      setShowPointsEarned(true);
      setTimeout(() => setShowPointsEarned(false), 2000);
    }
    
    // Award coins and XP immediately for the answer
    try {
      const response = await api.post('/education/answer-question', {
        module_id: selectedMustangQuiz.id,
        question_id: question.id || `q_${currentMustangQuestion}`,
        selected_answer: answerIndex,
        is_correct: isCorrect
      });
      
      if (response.data.coins_earned > 0) {
        toast.success(`+${response.data.coins_earned} coins, +${response.data.xp_earned} XP! 🎉`, {
          duration: 2000,
          icon: '🪙'
        });
      }
    } catch (error) {
      console.error('Failed to record answer:', error);
    }
  };

  const submitQuiz = async () => {
    setQuizCompleted(true);
    try {
      await api.post('/education/submit-quiz', {
        quiz_id: selectedQuiz.id,
        score: score + 1,
        total_questions: selectedQuiz.questions.length
      });
      toast.success(`Quiz completed! Earned ${(score + 1) * 10} bonus points!`);
      // Refresh quiz progress to show completion status
      fetchQuizProgress();
    } catch (error) {
      console.error('Error submitting quiz:', error);
    }
  };

  const submitMustangQuiz = async () => {
    try {
      await api.post('/education/submit-quiz', {
        quiz_id: selectedMustangQuiz.id,
        score: mustangScore,
        total_questions: selectedMustangQuiz.questions.length
      });
      toast.success(`Quiz completed! You can retake it anytime to earn more coins & XP!`);
      // Refresh quiz progress to show completion status
      fetchQuizProgress();
    } catch (error) {
      console.error('Error submitting quiz:', error);
    }
  };

  const nextMustangQuestion = () => {
    if (currentMustangQuestion < selectedMustangQuiz.questions.length - 1) {
      setCurrentMustangQuestion(currentMustangQuestion + 1);
      setCurrentMustangAnswer(null);
    } else {
      setMustangQuizCompleted(true);
      submitMustangQuiz();
    }
  };

  const openHmaDetails = (hma) => {
    setSelectedHma(hma);
    setShowHmaDialog(true);
  };

  return (
    <div className="min-h-screen" style={{backgroundColor: '#FCFCF8'}}>
      <Navigation user={user} logout={logout} />
      
      <div className="max-w-7xl mx-auto px-2 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-8">
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 sm:mb-8 fade-in" style={{color: '#D66238'}}>
          Learn & Explore
        </h1>

        <Tabs defaultValue="hmas" className="w-full">
          <TabsList className="mb-4 sm:mb-8 grid grid-cols-2 sm:grid-cols-4 h-auto">
            <TabsTrigger value="hmas" data-testid="tab-hmas" className="text-xs sm:text-sm py-2">HMAs</TabsTrigger>
            <TabsTrigger value="map" data-testid="tab-map" className="text-xs sm:text-sm py-2">Map</TabsTrigger>
            <TabsTrigger value="trivia" data-testid="tab-trivia" className="text-xs sm:text-sm py-2">Quizzes</TabsTrigger>
            <TabsTrigger value="resources" data-testid="tab-resources" className="text-xs sm:text-sm py-2">Resources</TabsTrigger>
          </TabsList>

          {/* HMAs TAB */}
          <TabsContent value="hmas">
            {/* Info Section */}
            <div className="mb-4 sm:mb-6 p-4 sm:p-6 rounded-2xl" style={{backgroundColor: '#6FCFD3', color: '#fff'}}>
              <h2 className="text-xl sm:text-2xl font-bold mb-2 sm:mb-3">About Herd Management Areas (HMAs)</h2>
              <p className="mb-2">
                Wild horses and burros are found on subsets of public lands, known as Herd Management Areas (HMAs), 
                across 10 western states covering 25.6 million acres.
              </p>
              <p>
                Explore {hmas.length} HMAs below to learn about wild mustang populations, appropriate management levels, 
                and the landscapes these magnificent animals call home.
              </p>
            </div>

            {/* Search and Filter */}
            <div className="mb-4 sm:mb-6 p-4 sm:p-6 rounded-2xl" style={{backgroundColor: '#fff', border: '2px solid #6FCFD3'}}>
              <div className="grid sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label className="block text-sm font-semibold mb-2" style={{color: '#D66238'}}>
                    <Search className="w-4 h-4 inline mr-2" />
                    Search HMAs
                  </label>
                  <Input
                    placeholder="Search by name or state..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2" style={{color: '#D66238'}}>
                    <Filter className="w-4 h-4 inline mr-2" />
                    Filter by State
                  </label>
                  <Select value={selectedState} onValueChange={setSelectedState}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All States</SelectItem>
                      {states.map(state => (
                        <SelectItem key={state} value={state}>{state}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <p className="text-sm mt-3" style={{color: '#D66238', opacity: 0.7}}>
                Found {filteredHmas.length} HMA{filteredHmas.length !== 1 ? 's' : ''}
              </p>
            </div>

            {/* HMA List */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {filteredHmas.map((hma, idx) => (
                <div
                  key={idx}
                  onClick={() => openHmaDetails(hma)}
                  data-testid={`hma-${hma.name.toLowerCase().replace(/\s+/g, '-')}`}
                  className="p-4 rounded-xl card-hover cursor-pointer"
                  style={{backgroundColor: '#fff', border: '2px solid #6FCFD3'}}
                >
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="text-lg font-bold flex-1" style={{color: '#D66238'}}>
                      {hma.name}
                    </h3>
                    <MapPin className="w-5 h-5 flex-shrink-0" style={{color: '#6FCFD3'}} />
                  </div>
                  <p className="text-sm font-semibold mb-3" style={{color: '#6FCFD3'}}>
                    {hma.state}
                  </p>
                  
                  {/* Fun Fact */}
                  {hma.fun_fact && (
                    <div className="mb-3 p-2 rounded-lg" style={{backgroundColor: '#FCFCF8', border: '1px solid #6FCFD3'}}>
                      <div className="flex items-start gap-1">
                        <Sparkles className="w-3 h-3 flex-shrink-0 mt-0.5" style={{color: '#D66238'}} />
                        <p className="text-xs italic" style={{color: '#D66238'}}>
                          {hma.fun_fact}
                        </p>
                      </div>
                    </div>
                  )}
                  
                  <div className="space-y-1 text-sm" style={{color: '#D66238'}}>
                    {hma.total_acres && (
                      <p><strong>Acres:</strong> {hma.total_acres.toLocaleString()}</p>
                    )}
                    {hma.horse_pop !== undefined && (
                      <p><strong>Horse Population:</strong> {hma.horse_pop}</p>
                    )}
                    {hma.burro_pop > 0 && (
                      <p><strong>Burro Population:</strong> {hma.burro_pop}</p>
                    )}
                    {hma.capacity && (
                      <p><strong>Capacity:</strong> {hma.capacity}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {filteredHmas.length === 0 && (
              <div className="text-center py-12">
                <p className="text-xl" style={{color: '#D66238'}}>
                  No HMAs found matching your criteria
                </p>
              </div>
            )}
          </TabsContent>

          {/* INTERACTIVE MAP TAB */}
          <TabsContent value="map">
            <div className="space-y-6">
              <div className="p-6 rounded-2xl" style={{backgroundColor: '#6FCFD3', color: '#fff'}}>
                <h2 className="text-2xl font-bold mb-3">Interactive HMA Map</h2>
                <p className="mb-2">
                  Explore all {filteredHmas.length} Herd Management Areas on the map below. Click markers to see details!
                </p>
                {selectedState !== 'all' && (
                  <p className="text-sm opacity-90">
                    Currently showing: {selectedState}
                  </p>
                )}
              </div>

              {/* Embedded Leaflet Map */}
              <div className="rounded-2xl overflow-hidden h-[400px] sm:h-[500px] lg:h-[600px]" style={{border: '2px solid #6FCFD3', position: 'relative', zIndex: 1}}>
                <MapContainer
                  center={[41.0, -112.0]}
                  zoom={5}
                  style={{ height: '100%', width: '100%', zIndex: 1 }}
                  scrollWheelZoom={true}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <FitBounds hmas={filteredHmas} />
                  {filteredHmas.map((hma, idx) => (
                    hma.lat && hma.lon && (
                      <Marker
                        key={idx}
                        position={[hma.lat, hma.lon]}
                        icon={hma.managing_agency === 'BLM Corral' ? blmCorralIcon : hma.managing_agency ? forestServiceIcon : blmIcon}
                      >
                        <Popup>
                          <div style={{ minWidth: '200px' }}>
                            <h3 className="font-bold text-lg mb-1" style={{color: '#D66238'}}>
                              {hma.name}
                            </h3>
                            <p className="text-sm font-semibold mb-1" style={{color: '#6FCFD3'}}>
                              {hma.city ? `${hma.city}, ${hma.state}` : hma.state}
                            </p>
                            {hma.managing_agency === 'BLM Corral' && (
                              <p className="text-xs font-medium mb-2 px-2 py-0.5 rounded inline-block" style={{backgroundColor: '#f97316', color: '#fff'}}>
                                🏠 Off-Range Corral
                              </p>
                            )}
                            {hma.managing_agency && hma.managing_agency !== 'BLM Corral' && (
                              <p className="text-xs font-medium mb-2 px-2 py-0.5 rounded inline-block" style={{backgroundColor: '#22c55e', color: '#fff'}}>
                                🌲 {hma.managing_agency}
                              </p>
                            )}
                            {!hma.managing_agency && (
                              <p className="text-xs font-medium mb-2 px-2 py-0.5 rounded inline-block" style={{backgroundColor: '#3b82f6', color: '#fff'}}>
                                BLM HMA
                              </p>
                            )}
                            {hma.fun_fact && (
                              <p className="text-xs italic mb-2" style={{color: '#D66238'}}>
                                <Sparkles className="w-3 h-3 inline mr-1" />
                                {hma.fun_fact}
                              </p>
                            )}
                            {hma.description && (
                              <p className="text-xs mb-2 text-gray-600">{hma.description}</p>
                            )}
                            <div className="text-xs space-y-1">
                              {hma.total_acres && (
                                <p><strong>Acres:</strong> {hma.total_acres.toLocaleString()}</p>
                              )}
                              {hma.horse_pop !== undefined && (
                                <p><strong>Horses:</strong> {hma.horse_pop}{hma.horse_aml !== undefined && ` (AML: ${hma.horse_aml})`}</p>
                              )}
                              {hma.burro_pop > 0 && (
                                <p><strong>Burros:</strong> {hma.burro_pop}{hma.burro_aml !== undefined && ` (AML: ${hma.burro_aml})`}</p>
                              )}
                              {hma.capacity && (
                                <p><strong>Capacity:</strong> {hma.capacity}</p>
                              )}
                              {hma.national_forest && (
                                <p><strong>National Forest:</strong> {hma.national_forest}</p>
                              )}
                              {hma.features && (
                                <p><strong>Features:</strong> {hma.features}</p>
                              )}
                              {hma.contact_phone && (
                                <p><strong>Phone:</strong> {hma.contact_phone}</p>
                              )}
                            </div>
                            {!hma.managing_agency || hma.managing_agency !== 'BLM Corral' ? (
                              <Button
                                onClick={() => openHmaDetails(hma)}
                                className="w-full mt-2 text-xs"
                                style={{backgroundColor: '#6FCFD3', color: '#fff'}}
                              >
                                View Details
                              </Button>
                            ) : null}
                          </div>
                        </Popup>
                      </Marker>
                    )
                  ))}
                </MapContainer>
              </div>

              {/* Map Controls Info */}
              <div className="p-4 rounded-xl" style={{backgroundColor: '#fff', border: '2px solid #6FCFD3'}}>
                <h4 className="text-sm font-bold mb-2" style={{color: '#D66238'}}>
                  Map Legend:
                </h4>
                <div className="flex flex-wrap gap-4 mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full" style={{backgroundColor: '#3b82f6'}}></div>
                    <span className="text-xs">BLM HMA</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full" style={{backgroundColor: '#22c55e'}}></div>
                    <span className="text-xs">Forest Service</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full" style={{backgroundColor: '#f97316'}}></div>
                    <span className="text-xs">Off-Range Corral</span>
                  </div>
                </div>
                <h4 className="text-sm font-bold mb-2" style={{color: '#D66238'}}>
                  Map Controls:
                </h4>
                <ul className="text-xs space-y-1" style={{color: '#D66238', opacity: 0.8}}>
                  <li>• Click any marker to see HMA details</li>
                  <li>• Use the +/- buttons to zoom in and out</li>
                  <li>• Click and drag to pan around the map</li>
                  <li>• Use the state filter above to focus on specific states</li>
                </ul>
              </div>

              {/* External Resources */}
              <div className="p-6 rounded-2xl" style={{backgroundColor: '#fff', border: '2px solid #6FCFD3'}}>
                <h3 className="text-xl font-bold mb-4" style={{color: '#D66238'}}>
                  Additional Map Resources
                </h3>
                <div className="space-y-3">
                  <Button
                    onClick={() => window.open('https://www.blm.gov/programs/wild-horse-and-burro/about-the-program/program-programs/interactive-web-maps', '_blank')}
                    className="w-full rounded-full"
                    style={{backgroundColor: '#6FCFD3', color: '#fff'}}
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Official BLM Interactive Map
                  </Button>
                  <Button
                    onClick={() => window.open('https://www.blm.gov/programs/wild-horse-and-burro/about-the-program/program-maps/maps-by-state', '_blank')}
                    className="w-full rounded-full"
                    style={{backgroundColor: '#6FCFD3', color: '#fff'}}
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Downloadable State Maps
                  </Button>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* TRIVIA TAB */}
          <TabsContent value="trivia">
            <div className="space-y-6">
              {/* Mustang Madness Interactive Module */}
              <div className="p-6 rounded-2xl" style={{backgroundColor: '#fff', border: '2px solid #6FCFD3'}}>
                <h2 className="text-2xl font-bold mb-4 flex items-center gap-2" style={{color: '#D66238'}}>
                  <Award className="w-6 h-6" />
                  Mustang Madness
                </h2>
                <p className="mb-6" style={{color: '#D66238', opacity: 0.8}}>
                  Learn essential Mustang knowledge through interactive multiple-choice questions based on official BLM & Forest Service guidelines.
                </p>
                
                {/* Module count indicator */}
                <div className="mb-4 p-3 rounded-lg flex items-center justify-between" style={{backgroundColor: '#E6F7F7', border: '1px solid #6FCFD3'}}>
                  <span className="font-bold" style={{color: '#065F46'}}>
                    📚 {mustangCareQuizzes.length} Quiz Modules Available
                  </span>
                  <button 
                    onClick={() => {
                      console.log('[QUIZ] Manual refresh triggered');
                      fetchMustangQuizzes();
                    }}
                    className="px-3 py-1 rounded text-sm font-medium"
                    style={{backgroundColor: '#6FCFD3', color: 'white'}}
                  >
                    🔄 Refresh Quizzes
                  </button>
                </div>
                
                {!mustangQuizStarted && !selectedModuleForGuide ? (
                  // Module selection view
                  <div className="space-y-4">
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {mustangCareQuizzes.map((quiz, idx) => {
                        const completed = isQuizCompleted(quiz.id);
                        const details = getQuizDetails(quiz.id);
                        return (
                        <div key={idx} className="p-4 rounded-xl relative" style={{backgroundColor: '#FCFCF8', border: `2px solid ${completed ? '#10B981' : '#6FCFD3'}`}}>
                          {completed && (
                            <div className="absolute -top-2 -right-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full font-bold">
                              ✓ Completed
                            </div>
                          )}
                          <h3 className="text-lg font-bold mb-2" style={{color: '#D66238'}}>
                            {quiz.title}
                          </h3>
                          <p className="text-sm mb-4" style={{color: '#D66238', opacity: 0.7}}>
                            {quiz.description}
                          </p>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs" style={{color: '#6FCFD3', fontWeight: 600}}>
                              {quiz.questions?.length || 0} Questions
                            </span>
                            <span className="text-xs" style={{color: '#6FCFD3', fontWeight: 600}}>
                              10 coins + 10 XP per correct answer
                            </span>
                          </div>
                          {details && (
                            <div className="text-xs mb-3 p-2 rounded" style={{backgroundColor: '#E6F7F7'}}>
                              <span style={{color: '#065F46'}}>
                                Best: {details.best_score}/{details.total_questions} • {details.attempts} attempt{details.attempts !== 1 ? 's' : ''}
                              </span>
                            </div>
                          )}
                          <div className="flex gap-2">
                            <Button
                              onClick={() => startMustangQuiz(quiz)}
                              className="flex-1 rounded-full"
                              style={{backgroundColor: '#6FCFD3', color: '#fff'}}
                            >
                              <Play className="w-4 h-4 mr-2" />
                              {completed ? 'Retake Quiz' : 'Start Quiz'}
                            </Button>
                            <Button
                              onClick={() => setSelectedModuleForGuide(quiz)}
                              className="flex-1 rounded-full"
                              variant="outline"
                              style={{borderColor: '#6FCFD3', color: '#6FCFD3'}}
                            >
                              <FileText className="w-4 h-4 mr-2" />
                              Study Guide
                            </Button>
                          </div>
                        </div>
                      )})}
                    </div>
                  </div>
                ) : mustangQuizStarted ? (
                  // Quiz view
                  <div className="max-w-2xl mx-auto">
                    {!mustangQuizCompleted ? (
                      <div className="space-y-4">
                        {/* Running Score Display */}
                        <div className="p-4 rounded-xl text-center" style={{backgroundColor: '#6FCFD3', color: '#fff'}}>
                          <div className="flex items-center justify-center gap-4">
                            <div>
                              <p className="text-sm font-semibold">Current Score</p>
                              <p className="text-3xl font-bold">{mustangScore} / {selectedMustangQuiz.questions.length}</p>
                            </div>
                            <div className="h-12 w-px" style={{backgroundColor: '#fff', opacity: 0.3}}></div>
                            <div>
                              <p className="text-sm font-semibold">Points Earned</p>
                              <p className="text-3xl font-bold">{mustangScore * 10}</p>
                            </div>
                          </div>
                        </div>

                        {/* Points Earned Animation */}
                        {showPointsEarned && (
                          <div className="fixed top-1/3 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50 animate-bounce">
                            <div className="p-6 rounded-2xl shadow-2xl" style={{backgroundColor: '#6FCFD3', color: '#fff'}}>
                              <p className="text-4xl font-bold">+10 Points! 🎉</p>
                            </div>
                          </div>
                        )}

                        <div data-testid="mustang-quiz-question" className="p-4 sm:p-8 rounded-2xl" style={{backgroundColor: '#FCFCF8', border: '2px solid #6FCFD3'}}>
                          <div className="mb-6">
                            <span className="text-sm" style={{color: '#6FCFD3', fontWeight: 600}}>
                              {selectedMustangQuiz.title} - Question {currentMustangQuestion + 1} of {selectedMustangQuiz.questions.length}
                            </span>
                          </div>
                          <h3 className="text-xl font-bold mb-6" style={{color: '#D66238'}}>
                            {selectedMustangQuiz.questions[currentMustangQuestion].question}
                          </h3>
                          
                          {/* Display question photo if it exists */}
                          {selectedMustangQuiz.questions[currentMustangQuestion].photo_url && (
                            <div className="mb-6">
                              <img
                                src={selectedMustangQuiz.questions[currentMustangQuestion].photo_url}
                                alt="Question illustration"
                                className="w-full max-w-md mx-auto rounded-lg shadow-md"
                              />
                            </div>
                          )}
                          
                          <div className="space-y-3">
                            {selectedMustangQuiz.questions[currentMustangQuestion].options.map((option, idx) => {
                              // Handle both old string format and new object format
                              const optionText = typeof option === 'object' ? option.text : option;
                              const optionPhoto = typeof option === 'object' ? option.photo_url : null;
                              
                              return (
                                <Button
                                  key={idx}
                                  data-testid={`mustang-answer-${idx}`}
                                  onClick={() => answerMustangQuestion(idx)}
                                  disabled={currentMustangAnswer !== null}
                                  className="w-full text-left rounded-xl text-sm disabled:opacity-50 disabled:cursor-not-allowed min-h-fit"
                                  style={{
                                    backgroundColor: '#fff', 
                                    color: '#D66238', 
                                    border: '2px solid #6FCFD3',
                                    padding: '12px 16px',
                                    lineHeight: '1.4',
                                    whiteSpace: 'normal',
                                    wordWrap: 'break-word',
                                    display: 'block'
                                  }}
                                >
                                  {optionPhoto && (
                                    <img src={optionPhoto} alt={`Option ${idx + 1}`} className="w-full h-24 object-cover rounded mb-2" />
                                  )}
                                  <span className="block">{optionText || '[Photo answer]'}</span>
                                </Button>
                              );
                            })}
                          </div>
                          {currentMustangAnswer !== null && (
                            <div className="mt-6">
                              <div className="p-4 rounded-xl" style={{
                                backgroundColor: currentMustangAnswer === selectedMustangQuiz.questions[currentMustangQuestion].correct ? '#D1FAE5' : '#FEE2E2',
                                border: '2px solid ' + (currentMustangAnswer === selectedMustangQuiz.questions[currentMustangQuestion].correct ? '#10B981' : '#EF4444')
                              }}>
                                <p className="font-semibold mb-2" style={{
                                  color: currentMustangAnswer === selectedMustangQuiz.questions[currentMustangQuestion].correct ? '#065F46' : '#991B1B'
                                }}>
                                  {currentMustangAnswer === selectedMustangQuiz.questions[currentMustangQuestion].correct ? '✅ Correct!' : '❌ Incorrect'}
                                </p>
                                <p className="text-sm" style={{color: '#374151'}}>
                                  {selectedMustangQuiz.questions[currentMustangQuestion].explanation}
                                </p>
                              </div>
                              <Button
                                onClick={nextMustangQuestion}
                                className="mt-4 rounded-full px-8"
                                style={{backgroundColor: '#6FCFD3', color: '#fff'}}
                              >
                                {currentMustangQuestion === selectedMustangQuiz.questions.length - 1 ? 'Finish Quiz' : 'Next Question'}
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="p-8 rounded-2xl text-center" style={{backgroundColor: '#fff', border: '2px solid #6FCFD3'}}>
                        <Award className="w-20 h-20 mx-auto mb-4" style={{color: '#D66238'}} />
                        <h3 className="text-3xl font-bold mb-4" style={{color: '#D66238'}}>Quiz Complete!</h3>
                        <p className="text-2xl mb-6" style={{color: '#6FCFD3'}}>
                          You scored {mustangScore} out of {selectedMustangQuiz.questions.length}
                        </p>
                        <p className="mb-6" style={{color: '#D66238'}}>
                          Earned {mustangScore * 10} points!
                          {mustangScore === selectedMustangQuiz.questions.length && " 🏆 Perfect Score!"}
                        </p>
                        <Button
                          onClick={() => {
                            setMustangQuizStarted(false);
                            setMustangQuizCompleted(false);
                            setMustangScore(0);
                            setCurrentMustangQuestion(0);
                            setCurrentMustangAnswer(null);
                          }}
                          className="rounded-full px-8"
                          style={{backgroundColor: '#6FCFD3', color: '#fff'}}
                        >
                          Back to Care Modules
                        </Button>
                      </div>
                    )}
                  </div>
                ) : selectedModuleForGuide ? (
                  <div className="max-w-4xl mx-auto">
                    {/* Study Guide View */}
                    <div className="p-6 rounded-2xl print:shadow-none" style={{backgroundColor: '#FCFCF8', border: '2px solid #6FCFD3'}}>
                      <div className="flex items-center justify-between mb-6 print:mb-4">
                        <div>
                          <h3 className="text-2xl font-bold" style={{color: '#D66238'}}>
                            {selectedModuleForGuide.title} - Study Guide
                          </h3>
                          <p className="text-sm mt-1" style={{color: '#D66238', opacity: 0.7}}>
                            {selectedModuleForGuide.description}
                          </p>
                        </div>
                        <div className="flex gap-2 print:hidden">
                          <Button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              
                              // Detect if on iOS/iPad
                              const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
                                           (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
                              
                              if (isIOS) {
                                // For iOS, create a new window with printable content
                                const printContent = document.querySelector('.prose');
                                const title = selectedModuleForGuide.title;
                                
                                if (!printContent) {
                                  alert('No content available to print');
                                  return;
                                }
                                
                                // Create a new window with the content
                                const printWindow = window.open('', '_blank');
                                if (!printWindow) {
                                  alert('Pop-up blocked. Please enable pop-ups for this site and try again.');
                                  return;
                                }
                                
                                // Write a clean HTML document for printing
                                printWindow.document.write(`
                                  <!DOCTYPE html>
                                  <html>
                                  <head>
                                    <meta charset="UTF-8">
                                    <meta name="viewport" content="width=device-width, initial-scale=1.0">
                                    <title>${title} - Study Guide</title>
                                    <style>
                                      body {
                                        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
                                        padding: 20px;
                                        max-width: 800px;
                                        margin: 0 auto;
                                        line-height: 1.6;
                                        color: #000;
                                      }
                                      h1, h2, h3, h4 {
                                        color: #000;
                                        margin-top: 1.5em;
                                        margin-bottom: 0.5em;
                                      }
                                      h1 { font-size: 24px; border-bottom: 2px solid #000; padding-bottom: 10px; }
                                      h4 { font-size: 18px; font-weight: bold; }
                                      p { margin-bottom: 0.5em; }
                                      .print-header {
                                        text-align: center;
                                        margin-bottom: 30px;
                                        padding-bottom: 20px;
                                        border-bottom: 3px solid #000;
                                      }
                                      .print-instructions {
                                        background: #f0f0f0;
                                        padding: 15px;
                                        border-radius: 8px;
                                        margin-bottom: 20px;
                                        text-align: center;
                                      }
                                      @media print {
                                        .print-instructions { display: none; }
                                      }
                                    </style>
                                  </head>
                                  <body>
                                    <div class="print-header">
                                      <h1>${title} - Study Guide</h1>
                                    </div>
                                    <div class="print-instructions">
                                      <strong>On iPad:</strong> Tap the Share button (square with arrow) at the top, then select "Print"
                                    </div>
                                    <div class="content">
                                      ${printContent.innerHTML}
                                    </div>
                                  </body>
                                  </html>
                                `);
                                printWindow.document.close();
                                
                                // Focus the new window
                                printWindow.focus();
                                
                              } else {
                                // Desktop/Android print handling
                                try {
                                  if (typeof window.print !== 'function') {
                                    alert('Print function is not available in your browser.');
                                    return;
                                  }
                                  
                                  setTimeout(() => {
                                    try {
                                      window.print();
                                    } catch (printError) {
                                      console.error('Error in window.print():', printError);
                                      alert('Unable to open print dialog. Please use Ctrl+P (Windows) or Cmd+P (Mac) to print.');
                                    }
                                  }, 100);
                                } catch (error) {
                                  console.error('Error in print handler:', error);
                                  alert('Unable to print. Please try using your browser\'s print function.');
                                }
                              }
                            }}
                            size="sm"
                            style={{backgroundColor: '#6FCFD3', color: '#fff'}}
                          >
                            <Printer className="w-4 h-4 mr-2" />
                            Print
                          </Button>
                          <Button
                            onClick={() => setSelectedModuleForGuide(null)}
                            size="sm"
                            variant="outline"
                          >
                            <X className="w-4 h-4 mr-2" />
                            Back
                          </Button>
                        </div>
                      </div>

                      {selectedModuleForGuide.outline_content && selectedModuleForGuide.outline_content.length > 0 ? (
                        <div className="prose max-w-none" style={{color: '#D66238'}}>
                          {selectedModuleForGuide.outline_content.map((item, idx) => {
                            // Check if item is a heading (starts with **)
                            if (item.startsWith('**') && item.endsWith('**')) {
                              return (
                                <h4 key={idx} className="text-lg font-bold mt-4 mb-2" style={{color: '#D66238'}}>
                                  {item.replace(/\*\*/g, '')}
                                </h4>
                              );
                            }
                            // Check if empty line
                            if (item.trim() === '') {
                              return <div key={idx} className="h-2"></div>;
                            }
                            // Regular item
                            return (
                              <p key={idx} className="text-sm mb-1 leading-relaxed" style={{color: '#D66238'}}>
                                {item}
                              </p>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="text-center py-12">
                          <FileText className="w-16 h-16 mx-auto mb-4" style={{color: '#6FCFD3', opacity: 0.5}} />
                          <p className="text-lg font-semibold mb-2" style={{color: '#D66238'}}>
                            Study Guide Not Available
                          </p>
                          <p className="text-sm" style={{color: '#D66238', opacity: 0.7}}>
                            This module does not have a study guide yet. Please try the quiz instead!
                          </p>
                          <Button
                            onClick={() => {
                              setSelectedModuleForGuide(null);
                              startMustangQuiz(selectedModuleForGuide);
                            }}
                            className="mt-4 rounded-full"
                            style={{backgroundColor: '#6FCFD3', color: '#fff'}}
                          >
                            <Play className="w-4 h-4 mr-2" />
                            Take Quiz Instead
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ) : null}
              </div>

              {/* General Trivia Quizzes - Removed */}
            </div>
          </TabsContent>

          {/* RESOURCES TAB */}
          <TabsContent value="resources">
            <div className="space-y-4 sm:space-y-6">
              {/* Dynamic Resources from Database */}
              {resources.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                  {resources.map((resource) => (
                    <div key={resource.id} className="p-4 sm:p-6 rounded-2xl" style={{backgroundColor: '#fff', border: '2px solid #6FCFD3'}}>
                      <h3 className="text-lg sm:text-xl font-bold mb-2 sm:mb-3" style={{color: '#D66238'}}>
                        {resource.title}
                      </h3>
                      <p className="mb-4" style={{color: '#D66238', opacity: 0.7}}>
                        {resource.description}
                      </p>
                      <a
                        href={resource.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold hover:opacity-80 transition-opacity"
                        style={{backgroundColor: '#6FCFD3', color: '#fff'}}
                      >
                        <ExternalLink className="w-4 h-4" />
                        Visit Resource
                      </a>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 p-6 rounded-2xl" style={{backgroundColor: '#fff', border: '2px solid #6FCFD3'}}>
                  <BookOpen className="w-16 h-16 mx-auto mb-4" style={{color: '#6FCFD3', opacity: 0.5}} />
                  <p style={{color: '#D66238', opacity: 0.7}}>
                    No resources available yet. Check back soon!
                  </p>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* HMA Details Dialog */}
      <Dialog open={showHmaDialog} onOpenChange={setShowHmaDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl flex items-center gap-2" style={{color: '#D66238'}}>
              <Mountain className="w-6 h-6" />
              {selectedHma?.name}
            </DialogTitle>
            <DialogDescription>
              <div className="mt-4 space-y-4">
                <div className="p-4 rounded-xl" style={{backgroundColor: '#6FCFD3', color: '#fff'}}>
                  <p className="text-lg font-semibold mb-2">{selectedHma?.state}</p>
                  <p className="text-sm opacity-90">Herd Management Area</p>
                  {selectedHma?.terrain && (
                    <p className="text-sm mt-2 opacity-90">
                      <strong>Terrain:</strong> {selectedHma.terrain}
                    </p>
                  )}
                </div>

                {/* Fun Fact - Featured */}
                {selectedHma?.fun_fact && (
                  <div className="p-4 rounded-xl" style={{backgroundColor: '#FCFCF8', border: '2px solid #6FCFD3'}}>
                    <div className="flex items-start gap-2">
                      <Sparkles className="w-5 h-5 flex-shrink-0 mt-0.5" style={{color: '#D66238'}} />
                      <div>
                        <h4 className="font-bold mb-1" style={{color: '#D66238'}}>Fun Fact</h4>
                        <p className="text-sm" style={{color: '#D66238'}}>
                          {selectedHma.fun_fact}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Description */}
                {selectedHma?.description && (
                  <div className="p-4 rounded-xl" style={{backgroundColor: '#fff', border: '2px solid #6FCFD3'}}>
                    <p className="text-sm" style={{color: '#D66238'}}>
                      {selectedHma.description}
                    </p>
                  </div>
                )}

                {/* Size Information - only show if data exists */}
                {(selectedHma?.total_acres || selectedHma?.blm_acres) && (
                  <div className="grid grid-cols-2 gap-4">
                    {selectedHma?.total_acres && (
                      <div className="p-4 rounded-xl" style={{backgroundColor: '#FCFCF8'}}>
                        <p className="text-sm font-semibold mb-1" style={{color: '#D66238'}}>Total Acreage</p>
                        <p className="text-2xl font-bold" style={{color: '#D66238'}}>
                          {selectedHma.total_acres.toLocaleString()}
                        </p>
                        <p className="text-xs mt-1" style={{color: '#D66238', opacity: 0.6}}>
                          {(selectedHma.total_acres / 640).toFixed(1)} square miles
                        </p>
                      </div>
                    )}
                    {selectedHma?.blm_acres && (
                      <div className="p-4 rounded-xl" style={{backgroundColor: '#FCFCF8'}}>
                        <p className="text-sm font-semibold mb-1" style={{color: '#D66238'}}>BLM Acres</p>
                        <p className="text-2xl font-bold" style={{color: '#D66238'}}>
                          {selectedHma.blm_acres.toLocaleString()}
                        </p>
                        {selectedHma?.total_acres && (
                          <p className="text-xs mt-1" style={{color: '#D66238', opacity: 0.6}}>
                            {Math.round((selectedHma.blm_acres / selectedHma.total_acres) * 100)}% BLM land
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Capacity - for corrals */}
                {selectedHma?.capacity && (
                  <div className="p-4 rounded-xl" style={{backgroundColor: '#FCFCF8'}}>
                    <p className="text-sm font-semibold mb-1" style={{color: '#D66238'}}>Facility Capacity</p>
                    <p className="text-2xl font-bold" style={{color: '#D66238'}}>
                      {selectedHma.capacity}
                    </p>
                  </div>
                )}

                {/* Horse Population */}
                {selectedHma?.horse_aml > 0 && (
                  <div className="p-4 rounded-xl" style={{backgroundColor: '#fff', border: '2px solid #6FCFD3'}}>
                    <h4 className="font-semibold mb-3" style={{color: '#D66238'}}>Horse Population</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm" style={{color: '#D66238', opacity: 0.7}}>Current Population</p>
                        <p className="text-xl font-bold" style={{color: '#6FCFD3'}}>
                          {selectedHma?.horse_pop}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm" style={{color: '#D66238', opacity: 0.7}}>Management Level (AML)</p>
                        <p className="text-xl font-bold" style={{color: '#6FCFD3'}}>
                          {selectedHma?.horse_aml}
                        </p>
                      </div>
                    </div>
                    {selectedHma?.horse_pop && selectedHma?.horse_aml && (
                      <div className="mt-3 p-2 rounded" style={{
                        backgroundColor: selectedHma.horse_pop > selectedHma.horse_aml ? '#FEE2E2' : '#D1FAE5'
                      }}>
                        <p className="text-xs text-center" style={{
                          color: selectedHma.horse_pop > selectedHma.horse_aml ? '#991B1B' : '#065F46'
                        }}>
                          {selectedHma.horse_pop > selectedHma.horse_aml
                            ? `${Math.round((selectedHma.horse_pop / selectedHma.horse_aml - 1) * 100)}% over target`
                            : selectedHma.horse_pop < selectedHma.horse_aml
                            ? `${Math.round((1 - selectedHma.horse_pop / selectedHma.horse_aml) * 100)}% below target`
                            : 'At target level'}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Burro Population */}
                {selectedHma?.burro_pop > 0 && (
                  <div className="p-4 rounded-xl" style={{backgroundColor: '#fff', border: '2px solid #6FCFD3'}}>
                    <h4 className="font-semibold mb-3" style={{color: '#D66238'}}>Burro Population</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm" style={{color: '#D66238', opacity: 0.7}}>Current Population</p>
                        <p className="text-xl font-bold" style={{color: '#6FCFD3'}}>
                          {selectedHma?.burro_pop}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm" style={{color: '#D66238', opacity: 0.7}}>Management Level (AML)</p>
                        <p className="text-xl font-bold" style={{color: '#6FCFD3'}}>
                          {selectedHma?.burro_aml}
                        </p>
                      </div>
                    </div>
                    {selectedHma?.burro_pop && selectedHma?.burro_aml && (
                      <div className="mt-3 p-2 rounded" style={{
                        backgroundColor: selectedHma.burro_pop > selectedHma.burro_aml ? '#FEE2E2' : '#D1FAE5'
                      }}>
                        <p className="text-xs text-center" style={{
                          color: selectedHma.burro_pop > selectedHma.burro_aml ? '#991B1B' : '#065F46'
                        }}>
                          {selectedHma.burro_pop > selectedHma.burro_aml
                            ? `${Math.round((selectedHma.burro_pop / selectedHma.burro_aml - 1) * 100)}% over target`
                            : selectedHma.burro_pop < selectedHma.burro_aml
                            ? `${Math.round((1 - selectedHma.burro_pop / selectedHma.burro_aml) * 100)}% below target`
                            : 'At target level'}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Notable Colors */}
                {selectedHma?.notable_colors && (
                  <div className="p-4 rounded-xl" style={{backgroundColor: '#FCFCF8', border: '2px solid #6FCFD3'}}>
                    <h4 className="font-semibold mb-2" style={{color: '#D66238'}}>Notable Colors & Patterns</h4>
                    <p className="text-sm" style={{color: '#D66238'}}>
                      {selectedHma.notable_colors}
                    </p>
                  </div>
                )}
              </div>
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const ResourceCard = ({ title, description, link }) => {
  return (
    <a href={link} target="_blank" rel="noopener noreferrer" data-testid={`resource-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="p-6 rounded-2xl card-hover h-full" style={{backgroundColor: '#fff', border: '2px solid #6FCFD3'}}>
        <h3 className="text-xl font-bold mb-3" style={{color: '#D66238'}}>{title}</h3>
        <p style={{color: '#D66238', opacity: 0.7}}>{description}</p>
      </div>
    </a>
  );
};

export default Learn;
